import React from 'react';
import { ChevronDown, Smartphone, Calendar, Award, Users } from 'lucide-react';
import { personalInfo } from '../../data/personal';
import { achievements } from '../../data/achievements';
import { HeroSectionProps } from '../../types';

const HeroSection: React.FC<HeroSectionProps> = ({ scrollTo }) => {
  const getIcon = (iconName: string) => {
    const iconMap = {
      Smartphone: <Smartphone className="w-8 h-8" />,
      Calendar: <Calendar className="w-8 h-8" />,
      Award: <Award className="w-8 h-8" />,
      Users: <Users className="w-8 h-8" />
    };
    return iconMap[iconName as keyof typeof iconMap] || null;
  };
  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20"
    >
      <div className="absolute inset-0 bg-grid opacity-[0.35]"></div>
      <div className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[52rem] h-[38rem] rounded-full bg-emerald-500/15 blur-[120px] pointer-events-none"></div>
      <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
        <p className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-wide mb-4 gradient-text bg-gradient-to-r from-emerald-400 to-emerald-300 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          {personalInfo.name}
        </p>

        <h1 className="spotlight text-4xl sm:text-5xl md:text-7xl font-bold mb-6 gradient-text bg-gradient-to-r from-emerald-400 via-gold-400 to-gold-300 animate-fade-in-up">
          {personalInfo.title}
        </h1>

        <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.2s'}}>
          {personalInfo.subtitle} with{" "}
          <span className="text-emerald-400 font-semibold">5+ years</span> of
          experience — {personalInfo.description}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in-up" style={{animationDelay: '0.4s'}}>
          <a
            href="https://wa.me/+963996697253"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-6 sm:px-8 py-4 rounded-full font-semibold transform hover:scale-[1.03] text-center"
          >
            Get In Touch
          </a>
          <button
            onClick={() => scrollTo("projects")}
            className="btn-outline px-6 sm:px-8 py-4 rounded-full font-semibold transform hover:scale-[1.03]"
          >
            View My Work
          </button>
        </div>

        {/* Achievement Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-16">
          {achievements.map((achievement, index) => (
            <div
              key={index}
              className="lit-card text-center p-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-1.5 animate-fade-in-up cursor-pointer"
              style={{animationDelay: `${0.6 + index * 0.1}s`}}
            >
              <div className="text-emerald-400 flex justify-center mb-2 drop-shadow-[0_0_10px_rgba(16,185,129,0.5)]">
                {getIcon(achievement.icon)}
              </div>
              <div className="text-xl sm:text-2xl font-bold text-white">
                {achievement.number}
              </div>
              <div className="text-slate-400 text-xs sm:text-sm">
                {achievement.label}
              </div>
            </div>
          ))}
        </div>

        {/* Arrow Down */}
        <div className="flex justify-center mb-16">
          <div className="animate-bounce">
            <ChevronDown className="w-8 h-8 text-slate-400" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;